import classNames from 'classnames';
import React from 'react';
import { Container } from 'src/components/layouts/Container/Container';

import CarrierCard from 'src/components/ui/CarrierCard/CarrierCard';
import HeadlineWithButtons from 'src/components/ui/HeadlineWithButtons/HeadlineWithButtons';
import { useTranslation } from 'react-i18next';
import { iconComponents, IconName } from 'src/components/ui/Icon/Icon';
import {
  CarrierAnalyticsData,
  progressToTrend,
  shareToPercent,
  toShipmentsBarChartKpi,
  useCarrierAnalytics,
} from 'src/services/carrierKpi';
import { TFunction } from 'i18next';
import styles from './styles/CarrierAnalytics.module.scss';

export const carrierIcon = (code: string): IconName | undefined =>
  code in iconComponents ? (code as IconName) : undefined;

export const buildShareKpiRows = (data: CarrierAnalyticsData, t: TFunction) =>
  [
    data.onTimeDelivery && {
      label: t('carrier_analytics.on_time_delivery'),
      score: shareToPercent(data.onTimeDelivery),
      trend: progressToTrend(data.onTimeDelivery),
    },
    data.firstAttempt && {
      label: t('carrier_analytics.first_attempt'),
      score: shareToPercent(data.firstAttempt),
      trend: progressToTrend(data.firstAttempt),
    },
    data.parcelShop && {
      label: t('carrier_analytics.parcel_shop'),
      score: shareToPercent(data.parcelShop),
      trend: progressToTrend(data.parcelShop),
    },
    data.returnsNonDelivery && {
      label: t('carrier_analytics.returns_non_delivery'),
      score: shareToPercent(data.returnsNonDelivery),
      trend: progressToTrend(data.returnsNonDelivery),
    },
  ].filter((row): row is { label: string; score: number; trend: number } => Boolean(row));

function CarrierAnalytics() {
  const { t } = useTranslation();
  const { carriers, isLoading } = useCarrierAnalytics();

  return (
    <Container className={classNames(styles.root)}>
      <HeadlineWithButtons headline={t('navbar.carrier_analytics')} />

      {!isLoading && carriers.length === 0 && (
        <p className="typo-text-14 text-faded">{t('carrier_analytics.no_data')}</p>
      )}
      {carriers.length > 0 && (
        <div className="pb-32 grid grid-cols-1 lg:grid-cols-2 gap-x-20 gap-y-20">
          {carriers.map(carrier => (
            <CarrierCard
              key={carrier.code}
              name={carrier.name}
              icon={carrierIcon(carrier.code)}
              detailsPath={`/carrier-analytics/${carrier.code}`}
              keyValuePairs={buildShareKpiRows(carrier.data, t)}
              chart={toShipmentsBarChartKpi(
                carrier.data.shipmentsPerDay,
                t('carrier_analytics.packages_per_day'),
                t('carrier_analytics.delivered_packages'),
              )}
            />
          ))}
        </div>
      )}
    </Container>
  );
}

export default CarrierAnalytics;
